import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { haptic } from '@/shared/lib/telegram';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const variantClasses: Record<Variant, string> = {
  primary:
    'bg-brand-telegram text-white hover:brightness-110 active:brightness-95 disabled:bg-bg-elevated disabled:text-text-tertiary',
  secondary:
    'bg-bg-surface-2 text-text-primary border border-divider hover:bg-bg-elevated disabled:opacity-60',
  ghost:
    'bg-transparent text-text-primary hover:bg-bg-surface-2 disabled:opacity-60',
  danger:
    'bg-danger text-white hover:brightness-110 disabled:opacity-60',
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-9 px-3 text-sm rounded-lg',
  md: 'h-11 px-4 text-base rounded-xl',
  lg: 'h-14 px-5 text-base font-semibold rounded-2xl',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    loading = false,
    leftIcon,
    rightIcon,
    className,
    children,
    onClick,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      onClick={(e) => {
        haptic('light');
        onClick?.(e);
      }}
      className={cn(
        'inline-flex items-center justify-center gap-2 font-medium transition select-none',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-telegram/40',
        variantClasses[variant],
        sizeClasses[size],
        fullWidth && 'w-full',
        loading && 'opacity-80 cursor-progress',
        className,
      )}
      {...rest}
    >
      {loading ? (
        <span className="inline-block h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
      ) : (
        leftIcon
      )}
      <span>{children}</span>
      {!loading && rightIcon}
    </button>
  );
});
